import { startOfDay } from 'date-fns';
import type { ActiveTimer, Mode, Session, Task } from '@timato/shared';
import { timerDeviceId } from '@/auth/thisDevice';
import { useCore } from '@/data/core';
import { saveRow } from '@/data/repo';
import { setTaskFinished } from '@/data/tasks';
import { db } from '@/db/db';
import { clockNow } from '@/lib/clock';
import { deleteSession } from '@/data/sessions';
import { endForRestart, MIN_LOGGED_SEC, parkTrack, resumeParked } from './completion';
import { elapsedSec, IDLE_TIMER, isBreak, setGoal, setMode, start } from './engine';
import { goalFromSession, goalFromTask, sameGoal, type Goal } from './goal';
import { applyOutcome, claimToStart, transitionCtx } from './transitions';

type WorkMode = 'focus' | 'track';

/**
 * A timer to start from outside the Timer screen (TW-27): a session in the log, a task's "Start timer here".
 * `mode` absent keeps the timer's own mode, or Focus after a break; `labelIds` null takes the goal's labels.
 */
export type StartRequest = { goal: Goal; task: Task | null; mode?: WorkMode; labelIds: string[] | null };

/** Carry on with a logged session: the same mode, the same goal as it stands now, and its labels. */
export async function requestFromSession(s: Session): Promise<StartRequest> {
  return { ...await goalFromSession(s), mode: s.mode === 'track' ? 'track' : 'focus', labelIds: s.labelIds };
}

export const requestFromTask = (t: Task): StartRequest => ({ goal: goalFromTask(t), task: t, labelIds: null });
/** A goal as the goal field or a project gives it; its task is read, so a shared one is still claimed. */
export const requestFromGoal = async (goal: Goal): Promise<StartRequest> => ({ goal, task: (goal.taskId && await db.tasks.get(goal.taskId)) || null, labelIds: null });

/**
 * What starting would do, before anything is written, so the caller can ask first: whether the task has to be
 * reopened, and whether a live run would be ended. `same` is the run already going: nothing to start. `resume` is
 * the goal's run parked today (TW-32), which Track continues rather than starting afresh.
 */
export type StartPlan = { request: StartRequest; mode: WorkMode; current: ActiveTimer; same: boolean; replacing: boolean; reopening: boolean; resume: Session | null };

export async function planStart(request: StartRequest): Promise<StartPlan> {
  const current = (await db.activeTimer.get('active')) ?? IDLE_TIMER;
  const mode = request.mode ?? (isBreak(current.mode) ? 'focus' : current.mode as WorkMode);
  const live = current.status !== 'idle';
  const same = live && current.mode === mode && sameGoal(current, request.goal);
  const resume = mode === 'track' ? await parkedToday(request.goal, clockNow()) : null;
  return { request, mode, current, same, replacing: live && !same, reopening: !!request.task?.finishedAt, resume };
}

/**
 * The goal's run parked today, if any (TW-32). Only today's: resuming an older one would add today's time to a row
 * filed under another day, so that one waits in Sessions to be finished and the goal starts a new run.
 */
export async function parkedToday(goal: Goal, now: Date): Promise<Session | null> {
  const from = startOfDay(now).toISOString();
  const rows = await db.sessions.where('status').equals('parked').filter((s) => !s.deletedAt && s.startedAt >= from && sameGoal(s, goal)).toArray();
  return rows.sort((a, b) => b.startedAt.localeCompare(a.startedAt))[0] ?? null;
}

/** What a start did: the run it ended (`before`, and the session that logged it, if any) and the run it began. */
export type Started = { before: ActiveTimer; ended: Session | null; started: ActiveTimer };

/**
 * Starts what was planned: a done task is reopened, then claimed if it is shared, and nothing more happens if a
 * teammate holds it (null). Reopening goes first so it cannot write back the row from before the claim; finishing
 * a task releases its claim, so a done one is rarely held. Then a live run is ended, its time logged (or, with
 * `park`, parked to resume later: TW-32), and the new run starts with the request's labels, or resumes a parked one
 * with its own.
 */
export async function startPlanned({ request, mode, reopening, resume }: StartPlan, { park = false } = {}): Promise<Started | null> {
  const { goal, task, labelIds } = request;
  if (reopening && task) await setTaskFinished(task, false);
  if (!await claimToStart(task, mode)) return null;
  const settings = useCore.getState().settings;
  // Read again: a confirm dialog may have stood open while the old run ticked on or completed.
  const before = (await db.activeTimer.get('active')) ?? IDLE_TIMER;
  let base = before, ended: Session | null = null;
  if (base.status !== 'idle') {
    const ctx = await transitionCtx(base, settings);
    const outcome = park && base.mode === 'track' ? parkTrack(base, ctx) : endForRestart(base, ctx);
    await applyOutcome(outcome);
    ({ next: base, session: ended } = outcome);
  }
  const now = clockNow(), deviceId = timerDeviceId();
  const ready = { ...setGoal(base, goal), mode, labelIds: resume ? resume.labelIds : labelIds };
  const started = resume ? resumeParked(ready, resume, { now, deviceId }) : start(ready, { now, settings, deviceId });
  await saveRow('activeTimer', started);
  return { before, ended, started };
}

/**
 * Moves the clock to another goal at once (TW-30): the run on the clock is parked (TW-32), and Track resumes the
 * new goal's parked run or starts one. No questions, since switching is the point; the targets offered leave done
 * tasks out, and one that was ticked off meanwhile is refused rather than reopened behind the person's back.
 */
export async function switchTrack(request: StartRequest): Promise<Started | null> {
  const plan = await planStart({ ...request, mode: 'track' });
  if (plan.same || plan.reopening) return null;
  return startPlanned(plan, { park: true });
}

/**
 * Whether picking `goal` in the goal field switches rather than renames (TW-31). Renaming a Track run that has had
 * time moves all of it to the new goal and logs nothing for the old one, so there it switches, as resuming a parked timer does.
 * It still renames a run under a minute old (fixing the goal just after Start), one begun with no goal (naming it),
 * the same goal picked again, an idle timer, and Focus, where a pomodoro is one task.
 */
export function goalChangeSwitches(t: ActiveTimer, goal: Goal, now: Date): boolean {
  const named = !!(t.taskId || t.projectId || t.goalText.trim());
  return t.mode === 'track' && t.status !== 'idle' && named && !sameGoal(t, goal) && elapsedSec(t, now) >= MIN_LOGGED_SEC;
}

/**
 * "Rename instead" after a goal-field switch (TW-31): the session it parked is taken back and the old run goes on
 * under the new goal, as a rename would have left it. The run still holds all its time, so nothing is lost when it
 * had been resumed. Only while the run the switch began is still on the clock; after another switch or a Finish
 * there is nothing left to put back (false).
 */
export async function renameInstead({ before, ended, started }: Started, goal: Goal): Promise<boolean> {
  if ((await db.activeTimer.get('active'))?.runId !== started.runId) return false;
  if (ended) await deleteSession(ended);
  // The old run keeps its own id, so the session it logs later reuses (and revives) the row just taken back.
  await saveRow('activeTimer', setGoal(before, goal));
  return true;
}

/**
 * Start `mode` in place of the run on the clock (TW-33): what Start does on a mode tab picked while a timer runs.
 * A Track run is parked (TW-32), to be resumed from the parked timers; a countdown is stopped and logged as Skip
 * would log it. The goal stays, and a shared task is claimed first, so a refusal leaves the run untouched (null).
 */
export async function startModeInstead(mode: Mode): Promise<Started | null> {
  const before = (await db.activeTimer.get('active')) ?? IDLE_TIMER;
  if (!await claimToStart(before.taskId ? await db.tasks.get(before.taskId) : null, mode)) return null;
  const settings = useCore.getState().settings;
  let base = before, ended: Session | null = null;
  if (base.status !== 'idle') {
    const ctx = await transitionCtx(base, settings);
    const outcome = base.mode === 'track' ? parkTrack(base, ctx) : endForRestart(base, ctx);
    await applyOutcome(outcome);
    ({ next: base, session: ended } = outcome);
  }
  const started = start(setMode(base, mode), { now: clockNow(), settings, deviceId: timerDeviceId() });
  await saveRow('activeTimer', started);
  return { before, ended, started };
}
